import { supabase } from "~/integrations/supabase/client";
import type {
  IngredientStockMovement,
  IngredientType,
  StockAdjustmentFormData,
} from "~/types/purchasing";

export const stockMovementService = {
  /**
   * Get all movements for a specific oil type
   */
  async getOilMovements(oilTypeId: string): Promise<IngredientStockMovement[]> {
    const { data, error } = await supabase
      .from("ingredient_stock_movements")
      .select(
        `
        *,
        oil_type:oil_types(id, name, code),
        performed_by_profile:profiles!performed_by(id, name)
      `
      )
      .eq("oil_type_id", oilTypeId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data as IngredientStockMovement[];
  },

  /**
   * Get all movements for a specific raw material
   */
  async getRawMaterialMovements(
    rawMaterialId: string
  ): Promise<IngredientStockMovement[]> {
    const { data, error } = await supabase
      .from("ingredient_stock_movements")
      .select(
        `
        *,
        raw_material:raw_materials(id, name, code),
        performed_by_profile:profiles!performed_by(id, name)
      `
      )
      .eq("raw_material_id", rawMaterialId)
      .order("created_at", { ascending: false });

    if (error) throw error;
    return data as IngredientStockMovement[];
  },

  /**
   * Get movements by ingredient type and ID
   */
  async getMovements(
    ingredientType: IngredientType,
    ingredientId: string
  ): Promise<IngredientStockMovement[]> {
    if (ingredientType === "oil") {
      return this.getOilMovements(ingredientId);
    } else {
      return this.getRawMaterialMovements(ingredientId);
    }
  },

  /**
   * Create a manual stock adjustment
   */
  async createAdjustment(data: StockAdjustmentFormData): Promise<string> {
    const { data: result, error } = await supabase.rpc(
      "create_stock_adjustment",
      {
        p_ingredient_type: data.ingredient_type,
        p_oil_type_id: data.oil_type_id ?? null,
        p_raw_material_id: data.raw_material_id ?? null,
        p_quantity: data.quantity,
        p_unit: data.unit,
        p_notes: data.notes,
      }
    );

    if (error) throw error;
    return result as string;
  },

  /**
   * Get recent movements across all ingredients (for dashboard)
   */
  async getRecentMovements(limit = 20): Promise<IngredientStockMovement[]> {
    const { data, error } = await supabase
      .from("ingredient_stock_movements")
      .select(
        `
        *,
        oil_type:oil_types(id, name, code),
        raw_material:raw_materials(id, name, code),
        performed_by_profile:profiles!performed_by(id, name)
      `
      )
      .order("created_at", { ascending: false })
      .limit(limit);

    if (error) throw error;
    return data as IngredientStockMovement[];
  },

  /**
   * List adjustment movements across all ingredients (for the "Ajustes de Ingredientes" page).
   * Text search on item name/code is applied client-side (joined fields).
   */
  async getAdjustments(filters?: {
    ingredientType?: IngredientType | "all";
    plant?: string;
    fromDate?: string;
    toDate?: string;
  }): Promise<IngredientStockMovement[]> {
    let query = supabase
      .from("ingredient_stock_movements")
      .select(
        `
        *,
        oil_type:oil_types(id, name, code),
        raw_material:raw_materials(id, name, code),
        performed_by_profile:profiles!performed_by(id, name)
      `
      )
      .eq("movement_type", "adjustment")
      .order("created_at", { ascending: false })
      .limit(2000);

    if (filters?.ingredientType && filters.ingredientType !== "all") {
      query = query.eq("ingredient_type", filters.ingredientType);
    }
    if (filters?.plant && filters.plant !== "all") {
      query = query.eq("plant", filters.plant);
    }
    if (filters?.fromDate) query = query.gte("created_at", filters.fromDate);
    if (filters?.toDate) query = query.lte("created_at", filters.toDate);

    const { data, error } = await query;
    if (error) throw error;
    return (data ?? []) as IngredientStockMovement[];
  },
};
